<template>
  <div>
    <b-row>
      <b-col sm>
        <b-form-group
          description="Filter decks by name and username"
          label="Filter"
        >
          <template v-slot:label>
            <div class="d-flex justify-content-between">
              <span>Filter</span>
              <span
                tabindex="0"
                class="info-tooltip"
                v-b-tooltip.html="{ customClass: 'filter-help-tooltip' }"
                :title="filterTooltip"
              >
                <font-awesome-icon
                  title="Help"
                  :icon="['far', 'question-circle']"
                />
              </span>
            </div>
          </template>
          <b-form-input
            v-model="filter"
            type="search"
            @update="currentPage = 1"
            debounce="500"
          ></b-form-input>
        </b-form-group>
      </b-col>
      <b-col sm>
        <TagPicker
          :options="tags"
          v-model="selectedTags"
          label="Tags"
          description="Filter decks by tags"
        />
      </b-col>
    </b-row>
    <MatchupCount
      :officialDeckCount="officialDeckCount"
      :fanDeckCount="rows"
      v-slot="{ total1v1, total2v2 }"
    >
      <p>
        There are <strong>{{ rows.toLocaleString() }}</strong> fan decks with
        the current filters and
        <strong>{{ officialDeckCount.toLocaleString() }}</strong> official
        decks. That makes
        <strong>{{ total1v1.toLocaleString() }}</strong> possible 1-on-1
        match-ups and <strong>{{ total2v2.toLocaleString() }}</strong> possible
        2-on-2 match-ups.
      </p>
    </MatchupCount>
    <b-table
      :items="getDeckData"
      :fields="fields"
      :per-page="perPage"
      :current-page="currentPage"
      :filter="{ filter, tags: selectedTags }"
      :sort-by="initialSortBy"
      :sort-desc="initialSortDesc"
      @sort-changed="sortChanged"
      primary-key="id"
      responsive
      class="compact-table-sm"
    >
      <template v-slot:cell(deck_name)="deck">
        <div class="d-flex">
          <b-img
            :blank="!deck.item.deck_data.appearance.cardbackUrl"
            :src="deck.item.deck_data.appearance.cardbackUrl"
            :blank-color="deck.item.deck_data.appearance.highlightColour"
            class="user-deck-list-thumb mr-2"
          ></b-img>
          <router-link
            :to="{
              name: 'EditVersion',
              params: { familyId: deck.item.id, id: deck.item.versionID },
            }"
          >
            {{ deck.value }}
          </router-link>
        </div>
      </template>
      <template v-slot:cell(creator)="data">
        <router-link
          :to="{ name: 'UserDeckList', params: { username: data.value } }"
        >
          {{ data.value }}
        </router-link>
      </template>
      <template v-slot:cell(tags)="data">
        <template v-for="tag in data.value">
          <b-badge href="#" @click="addTag(tag.name)" :key="tag.slug">{{
            tag.name
          }}</b-badge>
          {{ " " }}
        </template>
      </template>
      <template v-slot:cell(first_published)="data">
        <template v-if="data.value">
          {{ data.value }}
        </template>
        <template v-else>
          <em
            ><small class="text-muted"
              >Unknown<sup
                class="info-tooltip"
                tabindex="0"
                v-b-tooltip
                title="Published before publication date was recorded"
                >?</sup
              ></small
            ></em
          >
        </template>
      </template>
      <template v-slot:head(likes)>
        <font-awesome-icon title="Likes" :icon="['far', 'thumbs-up']" />
      </template>
    </b-table>
    <b-pagination
      v-model="currentPage"
      v-if="rows > perPage"
      :total-rows="rows"
      :per-page="perPage"
      first-number
      last-number
    ></b-pagination>
  </div>
</template>

<script type="text/javascript">
import api from "@/api";
import dateFormat from "@/mixins/dateFormat";
import MatchupCount from "@/components/MatchupCount.vue";
import TagPicker from "@/components/TagPicker.vue";

export default {
  name: "DeckList",
  metaInfo: {
    title: "Fan Decks",
  },
  components: { MatchupCount, TagPicker },
  mixins: [dateFormat],
  data() {
    return {
      filter: "",
      filterTooltip: `<p>Search terms will be found anywhere in the deck name or user name.</p>
        <p>To group terms together, enclose them <code>"in quotes"</code>.</p>
        <p>To search for a term only in the deck name, prepend with <code>deck:</code> or <code>d:</code>.</p>
        <p>To search for a term only in the user name, prepend with <code>user:</code> or <code>u:</code>.</p>
        <p>To search for matches NOT containing a term, prepend with a minus sign (<code>-</code>).</p>
        `,
      selectedTags: [],
      tags: [],
      rows: 0,
      currentPage: 1,
      perPage: 20,
      initialSortBy:
        this.$store.state.userPreferences.deckListSortBy ?? "updated",
      initialSortDesc:
        this.$store.state.userPreferences.deckListSortDesc ?? true,
      fields: [
        {
          key: "deck_name",
          sortable: true,
        },
        {
          key: "creator",
          sortable: true,
        },
        {
          key: "tags",
          sortable: false,
        },
        {
          key: "likes",
          sortable: true,
          sortDirection: "desc",
        },
        {
          key: "first_published",
          sortable: true,
          sortDirection: "desc",
        },
        {
          key: "updated",
          sortable: true,
          sortDirection: "desc",
        },
      ],
    };
  },
  computed: {
    officialDeckCount() {
      return this.$store.state.umDbDeckList.filter(
        (deck) => deck.cards.length > 0
      ).length;
    },
  },
  methods: {
    addTag(tag) {
      this.selectedTags.push(tag);
    },
    getDeckData(ctx, callback) {
      const {
        sortBy,
        sortDesc,
        perPage,
        currentPage,
        filter: { filter, tags },
      } = ctx;
      api
        .getDecks({ sortBy, sortDesc, perPage, currentPage, filter, tags })
        .then((data) => {
          this.rows = data.total;
          const items = data.decks.map((deck) => {
            return {
              deck_name: deck.name,
              id: deck.id,
              versionID: deck.version_id,
              created: this.formatDate(deck.created_on, "MMM D, YYYY"),
              updated: this.formatDate(deck.updated_on, "MMM D, YYYY"),
              first_published: this.formatDate(
                deck.deck_first_published_on,
                "MMM D, YYYY"
              ),
              creator: deck.user,
              tags: deck.tags,
              likes: deck.likes,
              deck_data: deck.deck_data,
            };
          });
          callback(items);
        })
        .catch(() => {
          callback([]);
        });

      return null;
    },
    sortChanged({ sortBy, sortDesc }) {
      this.$store.commit("setPreference", {
        key: "deckListSortBy",
        value: sortBy,
      });
      this.$store.commit("setPreference", {
        key: "deckListSortDesc",
        value: sortDesc,
      });
    },
  },
  mounted() {
    api.getTags().then((tags) => {
      this.tags = tags;
    });
  },
};
</script>

<style lang="less">
.filter-help-tooltip .tooltip-inner {
  text-align: left;
}

.user-deck-list-thumb {
  @card-height: 4ex;
  height: @card-height;
  width: 0.71 * @card-height;
  border-radius: 0.05 * @card-height;
  object-fit: cover;
  flex: 0 0 auto;
  align-self: center;
}
</style>
